import { ApolloClient, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { createUploadLink } from "apollo-upload-client";

/**
 * Fetch implementation that reports upload progress.
 *
 * The browser `fetch` API cannot expose upload progress, so for requests that
 * pass an `onUploadProgress` callback (via Apollo `context.fetchOptions`) we use
 * `XMLHttpRequest` instead. All other requests fall through to native `fetch`,
 * leaving the rest of the GraphQL traffic untouched.
 */
const progressFetch: typeof fetch = (input, init) => {
  const options = init as (RequestInit & {
    onUploadProgress?: (percent: number) => void;
  }) | undefined;

  if (typeof options?.onUploadProgress !== "function") {
    return fetch(input as RequestInfo, init);
  }

  const uri = String(input);
  const { onUploadProgress } = options;

  return new Promise<Response>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || "POST", uri, true);

    const headers = options.headers as Record<string, string> | undefined;
    if (headers) {
      Object.keys(headers).forEach((key) =>
        xhr.setRequestHeader(key, headers[key]),
      );
    }

    xhr.onload = () => {
      const responseHeaders = new Headers();
      xhr
        .getAllResponseHeaders()
        .trim()
        .split(/[\r\n]+/)
        .forEach((line) => {
          const idx = line.indexOf(": ");
          if (idx > 0)
            responseHeaders.set(line.slice(0, idx), line.slice(idx + 2));
        });
      resolve(
        new Response(xhr.response, {
          status: xhr.status,
          statusText: xhr.statusText,
          headers: responseHeaders,
        }),
      );
    };

    xhr.onerror = () => reject(new TypeError("Network request failed"));
    xhr.ontimeout = () => reject(new TypeError("Network request timed out"));
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));

    if (xhr.upload) {
      xhr.upload.onprogress = (event: ProgressEvent) => {
        if (event.lengthComputable) {
          onUploadProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }

    const signal = options.signal;
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener("abort", () => xhr.abort());
    }

    xhr.send(options.body as XMLHttpRequestBodyInit);
  });
};

const uploadLink = createUploadLink({
  uri:
    process.env.REACT_APP_ENV === "development"
      ? process.env.REACT_APP_API_URI
      : process.env.REACT_APP_API_SERVER_URI,
  fetch: progressFetch,
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  // return the headers to the context so httpLink can read them
  //const { user } = useAppSelector(selectAuth);
  const userInfo = localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo") as string)
    : null;
  const token = userInfo ? userInfo.token : null;
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

export const apolloClient = new ApolloClient({
  link: authLink.concat(uploadLink),
  cache: new InMemoryCache(),
  connectToDevTools: true,
});
